import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listInbox } from "../data/messages";
import { listMyRequests, listRequestsInbox } from "../data/requests";
import { getTutorById } from "../data/tutors";
import { toId } from "../lib/subjects";
import { submitReview } from "../data/reviews";

/* ======================
   THEME
====================== */
const BRAND = {
  bg: "#272c39",
  primary: "#7269e3",
  lilac: "#a783a6",
  mint: "#98dea3",
};

/* ======================
   HELPERS
====================== */
const timeFmt = (iso, lng) => {
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return "";
  return new Intl.DateTimeFormat(lng, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(ts)
  );
};

function normalizeRole(role) {
  return String(role || "").trim().toUpperCase();
}

function safeTs(iso) {
  const ts = new Date(iso).getTime();
  return Number.isFinite(ts) ? ts : null;
}

function isActiveRequest(r) {
  const s = String(r?.status || "").toUpperCase();
  return s === "CHAT_ACTIVE" || !!r?.contactsUnlocked;
}

function isArchivedRequest(r) {
  const s = String(r?.status || "").toUpperCase();
  return ["CLOSED", "TRIAL_DONE", "BECAME_CLIENT"].includes(s);
}

function getSubjectLabel(t, subject) {
  if (!subject) return t("bookings.requests", { defaultValue: "Requests" });
  return t(`subjects.${toId(subject)}`, { defaultValue: String(subject) });
}

function threadKey(th) {
  if (th?.requestId) return `r:${th.requestId}`;
  return `p:${th?.peerId || "?"}`;
}

function normalizeThreadFromInbox(th) {
  return {
    key: threadKey(th),
    source: "messages",
    peerId: th.peerId,
    requestId: th.requestId || null,
    unread: Number(th.unread || 0),
    lastMessage: th.lastMessage || null,
    kind: th.kind || null,
    subject: th.subject || null,
    status: th.status || null,
    preferredAt: th.preferredAt || null,
    studentName: th.studentName || null,
    tutorName: th.tutorName || null,
    contactsUnlocked: th.contactsUnlocked ?? null,
    updatedAt: th.updatedAt || th?.lastMessage?.createdAt || th?.lastMessage?.at || null,
  };
}

function normalizeThreadFromRequest({ r, isTutor }) {
  const peerId = isTutor ? r?.studentId : r?.tutorId;

  return {
    key: `r:${r.id}`,
    source: "requests",
    peerId,
    requestId: r.id,
    unread: 0,
    lastMessage: null,
    kind: String(r?.kind || "TRIAL").toUpperCase(),
    subject: r?.subject || null,
    status: String(r?.status || "").toUpperCase(),
    preferredAt: r?.preferredAt || null,
    studentName: r?.studentName || null,
    tutorName: r?.tutorName || null,
    contactsUnlocked: !!r?.contactsUnlocked,
    updatedAt: r?.updatedAt || r?.createdAt || null,
  };
}

function getInitials(name) {
  const s = String(name || "").trim();
  if (!s) return "??";
  const parts = s.split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || "";
  const b = parts.length > 1 ? parts[1]?.[0] || "" : parts[0]?.[1] || "";
  return (a + b).toUpperCase() || "??";
}

function cardAccent(kind, status, contactsUnlocked) {
  const k = String(kind || "").toUpperCase();
  const s = String(status || "").toUpperCase();

  if (s === "CHAT_ACTIVE" || contactsUnlocked) return BRAND.mint;
  if (k === "TRIAL") return BRAND.lilac;
  if (s === "BECAME_CLIENT") return BRAND.mint;
  if (s === "TRIAL_DONE") return BRAND.lilac;
  if (s === "CLOSED") return "rgba(255,255,255,0.18)";
  return BRAND.primary;
}

function getPreviewText(t, preferred, lastText) {
  if (lastText) return lastText;
  if (preferred) return `${t("requests.preferredAt", { defaultValue: "Preferred" })}: ${preferred}`;
  return t("messages.noMessagesYet", { defaultValue: "No messages yet" });
}

/* ======================
   PAGE
====================== */
export default function Messages() {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage || i18n.language || "en";
  const navigate = useNavigate();

  const auth = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("auth") || "null");
    } catch {
      return null;
    }
  }, []);

  const role = normalizeRole(auth?.role);
  const isTutor = role === "TUTOR";

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [threads, setThreads] = useState([]);
  const [names, setNames] = useState({});

  const [tab, setTab] = useState("ACTIVE"); // ACTIVE | ARCHIVED
  const [q, setQ] = useState("");
  const [reviewModal, setReviewModal] = useState({ open: false, thread: null });

  const refresh = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    setErr("");

    try {
      const inbox = await listInbox().catch(() => []);
      const inboxThreads = (Array.isArray(inbox) ? inbox : []).map(normalizeThreadFromInbox);

      const reqsRaw = isTutor ? await listRequestsInbox() : await listMyRequests();
      const reqs = Array.isArray(reqsRaw) ? reqsRaw : [];

      const reqThreads = reqs
        .filter((r) => isActiveRequest(r) || isArchivedRequest(r))
        .map((r) => normalizeThreadFromRequest({ r, isTutor }));

      const map = new Map();
      for (const th of reqThreads) map.set(th.key, th);

      for (const th of inboxThreads) {
        const k = th.key;
        const prev = map.get(k);
        if (prev) {
          map.set(k, {
            ...prev,
            unread: Math.max(prev.unread || 0, th.unread || 0),
            lastMessage: th.lastMessage || prev.lastMessage || null,
            updatedAt: th.updatedAt || prev.updatedAt || null,
            kind: prev.kind || th.kind || null,
            subject: prev.subject || th.subject || null,
            status: prev.status || th.status || null,
            preferredAt: prev.preferredAt || th.preferredAt || null,
          });
        } else {
          map.set(k, th);
        }
      }

      const merged = Array.from(map.values());
      merged.sort((a, b) => (safeTs(b.updatedAt) ?? 0) - (safeTs(a.updatedAt) ?? 0));

      // Deduplicate by peerId — show one card per specialist/client
      // Keep the most active thread (CHAT_ACTIVE/unlocked preferred), then most recent
      const peerMap = new Map();
      for (const th of merged) {
        const pid = th.peerId;
        if (!pid) continue;
        const prev = peerMap.get(pid);
        if (!prev) {
          peerMap.set(pid, th);
        } else {
          const prevActive = prev.status === "CHAT_ACTIVE" || prev.contactsUnlocked;
          const thActive = th.status === "CHAT_ACTIVE" || th.contactsUnlocked;
          if (thActive && !prevActive) {
            peerMap.set(pid, th);
          } else if (prevActive && !thActive) {
            // keep prev
          } else {
            // same activity level — keep most recent (merged is already sorted by updatedAt desc)
            // so prev is already the more recent one, keep it
          }
        }
      }
      const deduped = Array.from(peerMap.values());
      deduped.sort((a, b) => (safeTs(b.updatedAt) ?? 0) - (safeTs(a.updatedAt) ?? 0));

      setThreads(deduped);
    } catch (e) {
      console.error("Messages refresh failed:", e);
      setThreads([]);
      setErr(e?.message || t("messages.loadFailed", { defaultValue: "Failed to load messages" }));
    } finally {
      setLoading(false);
    }
  }, [auth, isTutor, t]);

  useEffect(() => {
    if (!auth) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.token, refresh]);

  useEffect(() => {
    if (!auth || threads.length === 0) return;

    let alive = true;

    (async () => {
      const next = {};
      for (const th of threads) {
        const peerId = th.peerId;
        if (!peerId || names[peerId]) continue;

        if (isTutor) {
          next[peerId] = th.studentName || t("roles.student", { defaultValue: "Student" });
          continue;
        }

        if (th.tutorName && String(th.tutorName).trim()) {
          next[peerId] = String(th.tutorName).trim();
          continue;
        }

        try {
          const tutor = await getTutorById(peerId);
          next[peerId] =
            tutor?.profile?.displayName ||
            tutor?.name ||
            t("roles.tutor", { defaultValue: "Tutor" });
        } catch {
          next[peerId] = t("roles.tutor", { defaultValue: "Tutor" });
        }
      }

      if (alive && Object.keys(next).length) {
        setNames((p) => ({ ...p, ...next }));
      }
    })();

    return () => {
      alive = false;
    };
  }, [auth, threads, names, isTutor, t]);

  const activeCount = useMemo(() => {
    return (threads || []).filter((x) => {
      const s = String(x.status || "").toUpperCase();
      return s === "CHAT_ACTIVE" || x.contactsUnlocked === true || (!s && x.source === "messages");
    }).length;
  }, [threads]);

  const archivedCount = useMemo(() => {
    return (threads || []).filter((x) =>
      ["CLOSED", "TRIAL_DONE", "BECAME_CLIENT"].includes(String(x.status || "").toUpperCase())
    ).length;
  }, [threads]);

  const filtered = useMemo(() => {
    const arr = Array.isArray(threads) ? threads : [];

    const byTab =
      tab === "ACTIVE"
        ? arr.filter((x) => {
            const s = String(x.status || "").toUpperCase();
            return s === "CHAT_ACTIVE" || x.contactsUnlocked === true || (!s && x.source === "messages");
          })
        : arr.filter((x) => {
            const s = String(x.status || "").toUpperCase();
            return ["CLOSED", "TRIAL_DONE", "BECAME_CLIENT"].includes(s);
          });

    const qq = String(q || "").trim().toLowerCase();
    if (!qq) return byTab;

    return byTab.filter((th) => {
      const name = (names[th.peerId] || "").toLowerCase();
      const subj = String(th.subject || "").toLowerCase();
      const status = String(th.status || "").toLowerCase();
      return name.includes(qq) || subj.includes(qq) || status.includes(qq);
    });
  }, [threads, tab, q, names]);

  if (!auth) return <Navigate to="/login" replace />;

  return (
    <main className="min-h-screen text-white" style={{ background: BRAND.bg }}>
      <div className="mx-auto max-w-5xl px-4 pt-8 pb-28 md:pb-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
              {t("messages.title", { defaultValue: "Messages" })}
            </h1>
            <p className="mt-1 text-sm text-white/65">
              {t("messages.subtitle", { defaultValue: "Your chats and requests with an open chat." })}
            </p>
          </div>

          <div className="flex gap-2 sm:pt-1">
            <button
              onClick={refresh}
              className="rounded-xl border border-white/15 bg-white/[0.04] px-3 py-2 text-sm text-white/85 hover:bg-white/[0.08]"
            >
              {t("actions.refresh", { defaultValue: "Refresh" })}
            </button>

            <button
              onClick={() => navigate("/dashboard")}
              className="rounded-xl border border-white/15 bg-white/[0.04] px-3 py-2 text-sm text-white/85 hover:bg-white/[0.08]"
            >
              {t("actions.back", { defaultValue: "Back" })}
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-6 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-2">
              <TabButton active={tab === "ACTIVE"} onClick={() => setTab("ACTIVE")} count={activeCount}>
                {t("messages.tabs.active", { defaultValue: "Active" })}
              </TabButton>

              <TabButton
                active={tab === "ARCHIVED"}
                onClick={() => setTab("ARCHIVED")}
                count={archivedCount}
              >
                {t("messages.tabs.archived", { defaultValue: "Archived" })}
              </TabButton>
            </div>

            <div className="relative w-full sm:w-96">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35">
                <MagnifierIcon />
              </span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("messages.searchPlaceholder", {
                  defaultValue: "Search by name / subject / status…",
                })}
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] pl-9 pr-3 py-2.5 text-sm text-white/85 placeholder:text-white/35 outline-none focus:border-white/20"
              />
            </div>
          </div>
        </div>

        {/* Review modal */}
        <ReviewModal
          open={reviewModal.open}
          thread={reviewModal.thread}
          t={t}
          onClose={() => setReviewModal({ open: false, thread: null })}
          onSubmitted={() => {
            setReviewModal({ open: false, thread: null });
            refresh();
          }}
        />

        {/* List */}
        <div className="mt-5">
          {loading ? (
            <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-6 text-white/70">
              {t("common.loading", { defaultValue: "Loading…" })}
            </div>
          ) : err ? (
            <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-6 text-red-100">
              {err}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-10 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white/[0.05] ring-1 ring-white/10">
                <ChatIcon />
              </div>
              <div className="mt-4 text-white/85 font-semibold">
                {t("messages.empty", { defaultValue: "No conversations yet." })}
              </div>
              <div className="mt-1 text-sm text-white/55">
                {t("messages.emptyHint", {
                  defaultValue: "Once a chat is opened, it will appear here even without messages.",
                })}
              </div>
            </div>
          ) : (
            <ul className="grid gap-4">
              {filtered.map((th) => {
                const peerId = th.peerId;
                const last = th.lastMessage;

                const displayName =
                  (peerId && names[peerId]) ||
                  (isTutor ? th.studentName : th.tutorName) ||
                  t("roles.tutor", { defaultValue: "Tutor" });

                const subjectLabel = getSubjectLabel(t, th.subject);

                const preferred = th.preferredAt ? timeFmt(th.preferredAt, lng) : null;
                const when = th.updatedAt ? timeFmt(th.updatedAt, lng) : "";
                const lastText = last?.text?.trim() ? last.text.trim() : "";
                const preview = getPreviewText(t, preferred, lastText);

                const to = `/chat/${peerId}?name=${encodeURIComponent(displayName)}${
                  th.requestId ? `&requestId=${encodeURIComponent(th.requestId)}` : ""
                }`;

                const accent = cardAccent(th.kind, th.status, th.contactsUnlocked);
                const isArchived = ["CLOSED", "TRIAL_DONE", "BECAME_CLIENT"].includes(
                  String(th.status || "").toUpperCase()
                );
                const canRate = !isTutor && isArchived && th.requestId;

                return (
                  <li key={th.key}>
                    <div
                      className="rounded-2xl bg-white/[0.05] ring-1 ring-white/10"
                      style={{ boxShadow: "0 18px 40px rgba(0,0,0,0.18)" }}
                    >
                      <Link
                        to={to}
                        className="block hover:bg-white/[0.02] transition rounded-2xl"
                      >
                        <div className="flex gap-4 p-4">
                          {/* Accent bar */}
                          <div className="w-1.5 rounded-full" style={{ background: accent }} />

                          {/* Avatar */}
                          <div className="shrink-0">
                            <Avatar name={displayName} unread={th.unread} />
                          </div>

                          {/* Content */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 min-w-0">
                                  <p className="text-base font-semibold truncate">{displayName}</p>
                                  {th.unread > 0 ? (
                                    <span
                                      className="shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold"
                                      style={{ background: BRAND.primary }}
                                    >
                                      {th.unread > 9 ? "9+" : th.unread}
                                    </span>
                                  ) : null}
                                </div>

                                <div className="mt-0.5 text-sm text-white/70 truncate">{subjectLabel}</div>
                              </div>

                              <div className="shrink-0 text-xs text-white/55">{when}</div>
                            </div>

                            {/* Preview */}
                            <p className="mt-2 text-sm text-white/80 truncate">{preview}</p>
                          </div>

                          {/* CTA */}
                          <div className="hidden md:flex shrink-0 items-center">
                            <span
                              className="rounded-xl px-3 py-2 text-sm font-semibold"
                              style={{
                                background: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.10)",
                                color: "rgba(255,255,255,0.85)",
                              }}
                            >
                              {t("messages.open", { defaultValue: "Open" })}
                            </span>
                          </div>
                        </div>
                      </Link>

                      {/* Rate button for archived client threads */}
                      {canRate && (
                        <div className="border-t border-white/[0.06] px-4 pb-3 pt-2.5">
                          <button
                            onClick={() => setReviewModal({ open: true, thread: { ...th, displayName } })}
                            className="rounded-xl px-3 py-1.5 text-sm font-semibold transition hover:brightness-110"
                            style={{ background: "rgba(255,253,116,0.12)", color: "#fffd74", border: "1px solid rgba(255,253,116,0.25)" }}
                          >
                            ★ {t("bookings.actions.rateLesson", { defaultValue: "Rate session" })}
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </main>
  );
}

/* ======================
   UI PIECES
====================== */
function TabButton({ active, onClick, count, children }) {
  return (
    <button
      onClick={onClick}
      className="rounded-xl px-4 py-2 text-sm font-semibold ring-1 transition"
      style={{
        background: active ? BRAND.primary : "rgba(255,255,255,0.05)",
        borderColor: active ? "transparent" : "rgba(255,255,255,0.10)",
        color: active ? "white" : "rgba(255,255,255,0.85)",
      }}
    >
      <span className="inline-flex items-center gap-2">
        {children}
        <span className="rounded-md bg-black/20 px-2 py-0.5 text-xs">{count}</span>
      </span>
    </button>
  );
}

function Avatar({ name, unread = 0 }) {
  const initials = getInitials(name);
  const hasUnread = Number(unread) > 0;

  return (
    <div className="relative">
      <div
        className="flex h-12 w-12 items-center justify-center rounded-2xl text-sm font-semibold ring-1"
        style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.12)" }}
      >
        {initials}
      </div>

      {hasUnread ? (
        <span
          className="absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full ring-2"
          style={{ background: BRAND.primary, borderColor: BRAND.bg }}
        />
      ) : null}
    </div>
  );
}

function MagnifierIcon({ className = "h-4 w-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M21 21l-4.2-4.2m1.7-5.1a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChatIcon({ className = "h-6 w-6" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M4 5.5A3.5 3.5 0 017.5 2h9A3.5 3.5 0 0120 5.5v7A3.5 3.5 0 0116.5 16H10l-4.2 3.2c-.6.5-1.8.1-1.8-.8V5.5z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="text-2xl leading-none transition hover:scale-110"
          style={{ color: n <= value ? "#fffd74" : "rgba(255,255,255,0.25)" }}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function ReviewModal({ open, thread, t, onClose, onSubmitted }) {
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStars(0);
      setComment("");
      setError("");
      setDone(false);
      setSubmitting(false);
    }
  }, [open]);

  if (!open || !thread) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (stars === 0) return;
    setSubmitting(true);
    setError("");
    try {
      await submitReview({ requestId: thread.requestId, stars, comment });
      setDone(true);
      setTimeout(onSubmitted, 1200);
    } catch (err) {
      setError(
        err?.data?.error === "ALREADY_REVIEWED"
          ? t("profile.reviewErrorAlready", { defaultValue: "You already reviewed this specialist." })
          : t("profile.reviewErrorGeneral", { defaultValue: "Something went wrong. Please try again." })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 ring-1 ring-white/15"
        style={{ background: "#272c39" }}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">
            {t("profile.reviewLeave", { defaultValue: "Leave a review" })}
          </h2>
          <button onClick={onClose} className="text-white/50 hover:text-white text-xl leading-none">×</button>
        </div>

        <p className="mb-4 text-sm text-white/60">
          {thread.displayName}
        </p>

        {done ? (
          <div className="py-6 text-center text-[#98dea3] font-semibold">
            {t("profile.reviewSuccess", { defaultValue: "Review submitted!" })}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <div className="mb-2 text-sm text-white/80">
                {t("profile.reviewStarsLabel", { defaultValue: "Your rating" })}
              </div>
              <StarPicker value={stars} onChange={setStars} />
            </div>

            <div>
              <div className="mb-1 text-sm text-white/80">
                {t("profile.reviewCommentLabel", { defaultValue: "Comment (optional)" })}
              </div>
              <textarea
                rows={3}
                maxLength={1000}
                placeholder={t("profile.reviewCommentPlaceholder", { defaultValue: "Share your experience…" })}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full resize-y rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#7269e3]"
              />
            </div>

            {error && (
              <p className="rounded-xl bg-red-500/10 px-3 py-2 text-sm text-red-400 ring-1 ring-red-500/20">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || stars === 0}
              className="w-full rounded-xl bg-[#7269e3] px-4 py-2 font-semibold text-white hover:brightness-110 disabled:opacity-50"
            >
              {submitting
                ? t("profile.reviewSubmitting", { defaultValue: "Submitting…" })
                : t("profile.reviewSubmit", { defaultValue: "Submit review" })}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
